/**
 * Helper function to retry dynamic imports that may fail due to network issues
 * 
 * @param {Function} importFn - The dynamic import function to retry
 * @param {Object} options - Configuration options
 * @param {number} options.retries - Maximum number of retries (default: 3)
 * @param {number} options.delay - Base delay in ms between retries (default: 1000)
 * @param {boolean} options.backoff - Whether to use exponential backoff (default: true)
 * @returns {Promise} - The import promise
 */
export function retryImport(importFn, options = {}) {
  const { 
    retries = 3, 
    delay = 1000, 
    backoff = true 
  } = options;
  
  return new Promise((resolve, reject) => {
    // Function to attempt the import
    const attempt = (retryCount) => {
      importFn()
        .then(resolve)
        .catch((error) => {
          // If we have retries left, try again
          if (retryCount < retries) {
            const nextDelay = backoff 
              ? delay * Math.pow(2, retryCount) 
              : delay;
              
            console.warn(
              `Import failed, retrying (${retryCount + 1}/${retries}) in ${nextDelay}ms`, 
              error
            );
            
            setTimeout(() => attempt(retryCount + 1), nextDelay);
          } else {
            console.error('Import failed after maximum retries', error);
            reject(error);
          }
        });
    };
    
    // Start the first attempt
    attempt(0);
  });
}

export default retryImport; 